import React, { Component } from 'react';
import T from 'prop-types';
// redux, recompose
import { compose } from 'recompose';
import { connect } from 'react-redux';
import withDataTableController from 'components/DataTable/DataTableController/DataTableController';
import { fetchData } from 'reducers/dataTable';
import { fetchUserProfile } from 'reducers/profiles';
// components
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import TableFilter from 'components/TableFilter';
import SearchBox from 'components/SearchBox';
import Preloader from 'components/Preloader';
import Panel from 'components/Panel';
import DataTable from 'components/DataTable/DataTable';
import { ArrowColumn, CustomColumn } from 'components/DataTable/Columns';
import NothingBox from 'components/NothingBox';
import DropdownButton from 'components/DropdownButton';
import Button from 'components/Button';
import RolesLabel from 'components/RolesLabel/RolesLabel';
import Avatar from 'components/Avatar/Avatar';
import StatusBadge from 'components/StatusBadge/StatusBadge';
import RolesPlaceholder from 'components/Admin/RolesPlaceholder';
// utils
import isEmpty from 'lodash.isempty';
import sum from 'lodash.sum';
import cn from 'classnames';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { fetchFromAPI } from 'utils/api';
import { showToastrMessage } from 'utils';

class UserCompanyRoles extends Component {

  state = {
    isRoleRemoving: false,
  };

  componentDidMount() {
    const { fetchData, reqParams } = this.props;
    fetchData(reqParams).catch(toastResponseErrors);
  }

  onFilterChange = (filter) => {
    const { fetchData, reqParams } = this.props;
    fetchData({ ...reqParams, page: 1, role: filter });
  };

  revokeUserCompanyRole = ({ id: company_id, is_owner, is_caretaker, is_manager, is_common, is_vet }) => {
    const { params: { id: user_id }, authUser } = this.props;
    let name;
    if (is_owner) name = 'owner';
    if (is_caretaker) name = 'caretaker';
    if (is_manager) name = 'manager';
    if (is_common) name = 'common_user';
    if (is_vet) name = 'vet';
    const paramsData = { company_id, name, user_id };

    if ((authUser.current_company.id === company_id) && (name === 'common_user')) {
      showToastrMessage('component.toastr.currentCompany.cantRevokeRole', 'warning', 'fa fa-exclamation-triangle-bts');
      return;
    }

    this.removeRole(paramsData);
  };

  removeRole = (data) => {
    const { fetchData, reqParams, params: { id }, fetchUserProfile } = this.props;
    this.setState({ isRoleRemoving: true });
    return fetchFromAPI(`/admin/users/${id}/company_roles`, { method: 'delete', body: JSON.stringify(data) })
      .then(() => {
        this.setState({ isRoleRemoving: false });
        fetchData(reqParams).then(({ resources }) => {
          if (!resources.length) fetchUserProfile();
        });
        showToastrMessage('component.toastr.role.revoked');
      })
      .catch((response) => {
        this.setState({ isRoleRemoving: false });
        toastResponseErrors(response);
      });
  };

  renderCompanyNameColumn = ({ name }) => (
    <CustomColumn flexRow>
      <Avatar type="company" avatarSize="35" iconSize="16" />
      <span className="semibold ml-10">{name}</span>
    </CustomColumn>
  );

  renderRoleColumn = ({ is_owner, is_manager, is_caretaker, is_common, is_vet }) => (
    <CustomColumn flexRow>
      <FormattedMessage id="general.companyRole">
        {(text) => (<span className="hide-for-large semibold">{text}&nbsp;•&nbsp;</span>)}
      </FormattedMessage>
      <RolesLabel
        roles={[
          { name: <FormattedMessage id="general.role.primary" />, value: is_owner, shortcut: 'p' },
          { name: <FormattedMessage id="general.role.farmManager" />, value: is_manager, shortcut: 'm' },
          { name: <FormattedMessage id="general.role.farmCaregiver" />, value: is_caretaker, shortcut: 'c' },
          { name: <FormattedMessage id="general.role.common" />, value: is_common, shortcut: 'u' },
          { name: <FormattedMessage id="general.role.veterinarian" />, value: is_vet, shortcut: 'v' },
        ]}
      />
    </CustomColumn>
  );

  renderStatusColumn = ({ status }) => (
    <CustomColumn noBottomBorder label={<FormattedMessage id="component.dataTable.headers.status" />}>
      <StatusBadge status={status} />
    </CustomColumn>
  );

  renderActionsColumn = (company, { rowIndex }) => (
    <div className="collapsible-value button-column">
      <DropdownButton
        idKey={`${company.id}-${rowIndex}`}
        wide
        label={<FormattedMessage id="general.button.revokeAccess" />}
        buttonType="small light-gray"
        onClick={() => this.revokeUserCompanyRole(company)}
        dropDownData={[
          { label: <FormattedMessage id="general.editCompany" />, url: `/admin/companies/${company.id}/edit` },
        ]}
        customClass="show-for-large"
      />
      <Link to={`/admin/companies/${company.id}/edit`} className="button light hide-for-large">
        <FormattedMessage id="general.editCompany" />
      </Link>
      <Button darkGrey onClick={() => this.revokeUserCompanyRole(company)} className="hide-for-large">
        <FormattedMessage id="general.button.revokeAccess" />
      </Button>
    </div>
  );

  renderExpandable = (company) => (
    <div>
      <Link to={`/admin/companies/${company.id}/edit`} className="button small light">
        <i className="fa fa-pencil mr-5" />
        <FormattedMessage id="general.editCompany" />
      </Link>
      <Button small light onClick={() => this.revokeUserCompanyRole(company)}>
        <i className="fa fa-times-circle mr-5" />
        <FormattedMessage id="general.button.revokeAccess" />
      </Button>
    </div>
  );

  render() {
    const { resources, meta: { total, stats = {} }, isLoading, reqParams: { page, per_page, role, search, sort },
      params: { id }, onSearchChange, onPageChange, onPerPageChange, onSortChange } = this.props;
    const { isTablet } = this.context;
    const { isRoleRemoving } = this.state;
    const statsSum = sum(Object.values(stats));
    const tableFilters = [
      { label: <FormattedMessage id="general.allRoles" />, value: '', count: statsSum },
      { label: <FormattedMessage id="general.role.primary" />, value: 'owner' },
      { label: <FormattedMessage id="general.role.manager" />, value: 'manager' },
      { label: <FormattedMessage id="general.role.caregiver" />, value: 'caretaker' },
      { label: <FormattedMessage id="general.role.veterinarian" />, value: 'vet' },
      { label: <FormattedMessage id="general.role.common" />, value: 'common_user' },
    ];
    const isEmptyTable = !resources.length && !role && !search;

    const columns = [
      { label: <FormattedMessage id="general.companyName" />, flex: '2 0 120px',
        renderer: this.renderCompanyNameColumn, sortKey: 'name' },
      { label: <FormattedMessage id="general.companyRole" />, flex: '1 0 120px', renderer: this.renderRoleColumn,
        sortKey: 'role' },
      { label: <FormattedMessage id="component.dataTable.headers.status" />, flex: '1 0 120px',
        renderer: this.renderStatusColumn },
      { label: '', flex: '0 0 180px', renderer: this.renderActionsColumn, fixed: true,
        className: cn({ 'hide-for-large': isTablet }) },
      { label: '', flex: '0 0 40px', renderer: () => <ArrowColumn />, fixed: true, hide: !isTablet,
        hasPinnedIcon: true },
    ];

    const paginationProps = {
      onPageChange,
      onPerPageChange,
      totalItems: total,
      currentPage: page,
      perPage: per_page,
    };

    return (
      <Panel>
        <Panel.Heading title={<FormattedMessage id="general.companyRoles" />}>
          {!isEmptyTable &&
            <SearchBox initialValue={search} onChange={onSearchChange} />}
        </Panel.Heading>

        <Panel.Body noPadding>
          {!isEmptyTable && (
            <TableFilter
              filters={tableFilters}
              activeFilter={role}
              onFilterChange={this.onFilterChange}
              stats={stats}
              className="ph-10"
            >
              {!isEmpty(resources) && (
                <Link to={`/admin/users/${id}/assign-company-roles`} className="button small primary wider">
                  <FormattedMessage id="general.manageRoles" />
                </Link>
              )}
            </TableFilter>
          )}

          <Preloader isActive={isLoading || isRoleRemoving} />

          <DataTable
            data={resources}
            columns={columns}
            sort={sort}
            onSortChange={onSortChange}
            paginationProps={paginationProps}
            isExpandable={isTablet}
            renderExpandable={this.renderExpandable}
            scrollable
            tableKey="userCompanyRoles"
            rowKey="role_id" // todo: id is not unique for roles table now
          />

          <NothingBox
            itemsName="roles"
            display={!resources.length}
            isLoading={isLoading}
            search={search}
            filter={role}
          >
            <FormattedMessage id="component.nothingBox.noCompanyRoles" tagName="h1" />
            <Link to={`/admin/users/${id}/assign-company-roles`} className="button primary mt-10">
              <FormattedMessage id="general.assignCompanyRoles" />
            </Link>
            <RolesPlaceholder />
          </NothingBox>
        </Panel.Body>
      </Panel>
    );
  }
}

UserCompanyRoles.propTypes = {
  params: T.object,
  fetchUserProfile: T.func.isRequired,
  fetchData: T.func.isRequired,
  onSearchChange: T.func.isRequired,
  onPageChange: T.func.isRequired,
  onPerPageChange: T.func.isRequired,
  onSortChange: T.func.isRequired,
  authUser: T.object.isRequired,
  resources: T.array.isRequired,
  meta: T.object.isRequired,
  reqParams: T.object.isRequired,
  isLoading: T.bool.isRequired,
};

UserCompanyRoles.contextTypes = {
  isTablet: T.bool.isRequired,
};

const enhance = compose(
  connect((state) => ({
    user: state.profiles.user.data,
    resources: state.dataTable.userCompanyRoles.resources,
    reqParams: state.dataTable.userCompanyRoles.params,
    meta: state.dataTable.userCompanyRoles.meta,
    isLoading: state.dataTable.userCompanyRoles.isLoading,
    authUser: state.auth.user,
  }), (dispatch, { params: { id } }) => ({
    fetchUserProfile: () => dispatch(fetchUserProfile(`/admin/users/${id}`)),
    fetchData: (query) => dispatch(fetchData(`/admin/users/${id}/company_roles/profile_index`,
      'userCompanyRoles', query)),
  })),
  withDataTableController('fetchData', 'reqParams'),
);

export default enhance(UserCompanyRoles);
